<script setup>
import BaseModal from '@/components/global-themed/BaseModal.vue';
import ModalHeading from '@/components/global-themed/ui/modals/ModalHeading.vue';
import ModalContentWrapper from '@/components/global-themed/ui/modals/ModalContentWrapper.vue';
import DefaultButton from '@/components/global-themed/buttons/DefaultButton.vue';
import { successToast } from '@/composables/useToasts';
import {computed, ref} from 'vue';
import axios from 'axios';
import moment from "moment";

const props = defineProps({
  websiteId: {
    type: Number,
    required: true,
  },
  websiteUrl: {
    type: String,
    default: '',
  },
  deletedAt: {
    type: [String, null],
    default: null,
  }
});

const emit = defineEmits(['close', 'restored']);

const showModal = ref(false);
const loading = ref(false);
const errorMessage = ref(null);

function openModal() {
  errorMessage.value = null;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  errorMessage.value = null;
  emit('close');
}

const softDeletesDate = moment('2026-05-12 00:00:00');

const deletedAtDate = computed(() => props.deletedAt ? moment(props.deletedAt) : null);

const serpDataDeletedDate = computed(() => {
  if (!props.deletedAt) {
    return null;
  }
  if (deletedAtDate.value.isBefore(softDeletesDate)) {
    return deletedAtDate.value;
  }

  return deletedAtDate.value.clone().add(30, 'days');
})

const SERPDataDeleted = computed(() => {
  if (!props.deletedAt) {
    return false;
  }
  if (deletedAtDate.value.isBefore(softDeletesDate)) {
    return true;
  }

  return deletedAtDate.value.isBefore(moment().subtract(30, 'days'))
});

const displayText = computed(() => SERPDataDeleted.value
  ? 'We will restore the website and start tracking SERP ranking data again from today.'
  : 'SERP ranking data will be restored for this website. There may be a gap in the ranking data if its been more than a week since the website was deleted.'
);

function handleRestore() {
  loading.value = true
  errorMessage.value = null;
  axios
    .post(`/admin/deleted-accounts/websites/${props.websiteId}/restore`)
    .then(() => {
      emit('restored');
      closeModal();
      successToast('Website has been successfully restored.', 'Success', {
        timeout: 5000,
        link: {
          url: `/website/settings/${props.websiteId}`,
          text: 'View settings',
        },
        onDismiss: () => window.location.reload(),
      });
    })
    .catch((error) => {
      errorMessage.value =
        error.response?.data?.message ?? 'Something went wrong restoring the website.';
    })
    .finally(() => {
      loading.value = false;
    });
}

</script>

<template>
  <div class="tw-w-full tw-flex">
    <DefaultButton
      type="button"
      size="small"
      @click="openModal"
    >
      Restore
    </DefaultButton>
    <BaseModal
      v-if="showModal"
      modal-class="tw-w-[700px]"
      @close="closeModal"
    >
      <ModalContentWrapper>
        <ModalHeading>Restore {{ websiteUrl }}</ModalHeading>
        <div
          :class="[
            'tw-border-2 tw-p-2 tw-rounded-lg tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-1.5',
            SERPDataDeleted ? 'tw-bg-danger-100 tw-border-danger ' : 'tw-bg-success-100 tw-border-success'
          ]"
        >
          <div
            class="tw-flex tw-items-center tw-gap-2"
            :class="SERPDataDeleted ? 'tw-text-danger-700' : 'tw-text-success-700'"
          >
            <SvgVue
              :icon="SERPDataDeleted ? 'icons/cross' : 'icons/tick'"
              class="tw-w-4 tw-h-4 tw-shrink-0"
            />
            <span class="tw-font-bold">
              SERP data {{ SERPDataDeleted ? 'unavailable' : 'available' }}
            </span>
          </div>
          <span>
            The SERP ranking data {{ SERPDataDeleted ? 'was deleted on' : 'is available until' }} {{ serpDataDeletedDate.format('DD/MM/YYYY h:mm:ss') }}.
          </span>
        </div>
        <p>
          {{ displayText }}
        </p>
        <div class="tw-flex tw-gap-3">
          <div class="tw-w-full tw-flex tw-flex-col tw-gap-3">
            <DefaultButton
              class="tw-w-full"
              :disabled="loading"
              @click="handleRestore"
            >
              Restore Website
            </DefaultButton>
          </div>
        </div>
        <p
          v-if="errorMessage"
          class="tw-text-danger tw-mt-4 tw-text-sm"
        >
          {{ errorMessage }}
        </p>
      </ModalContentWrapper>
    </BaseModal>
  </div>
</template>
